import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface Role {
  Id: number;
  Name: string;
  Description?: string;
  Active: boolean;
}

export interface User {
  Id: number;
  FirstName: string;
  LastName: string;
  Email?: string;
  Active: boolean;
}

export interface Status {
  Id: number;
  StatusName: string;
  Description?: string;
  Active: boolean;
}

export interface EventType {
  Id: number;
  Name: string;
  Description?: string;
  Active: boolean;
}

export interface Group {
  Id: number;
  Name: string;
  Description?: string;
  Active: boolean;
}

export interface ApprovalLevel {
  Id?: number;
  Level: number;
  ApprovedById: number;
  ApprovedBy?: Role;
  
  // Approve fields
  ApproveStatusId: number;
  ApproveStatus?: Status;
  ApproveEventTypeId?: number;
  ApproveEventType?: EventType;
  ApproveGroupId?: number;
  ApproveGroup?: Group;
  
  // Reject fields
  RejectStatusId: number;
  RejectStatus?: Status;
  RejectEventTypeId?: number;
  RejectEventType?: EventType;
  RejectGroupId?: number;
  RejectGroup?: Group;
  
  // Pending fields
  PendingStatusId: number;
  PendingStatus?: Status;
  PendingEventTypeId?: number;
  PendingEventType?: EventType;
  PendingGroupId?: number;
  PendingGroup?: Group;
  
  WorkFlowSetupId?: number;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface Workflow {
  Id: number;
  ProcessName: string;
  Roles?: Role[];
  Active: boolean;
  ApprovalLevels?: ApprovalLevel[];
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface CreateWorkflowRequest {
  ProcessName: string;
  Roles: Role[];
  Active: boolean;
  ApprovalLevels: Omit<ApprovalLevel, 'Id' | 'WorkFlowSetupId' | 'CreatedDate' | 'ModifiedDate'>[];
}

export interface UpdateWorkflowRequest {
  Id: number;
  ProcessName: string;
  Roles: Role[];
  Active: boolean;
  ApprovalLevels: Omit<ApprovalLevel, 'WorkFlowSetupId' | 'CreatedDate' | 'ModifiedDate'>[];
}

export interface CreateApprovalLevelRequest {
  Level: number;
  ApprovedById: number;
  ApproveStatusId: number;
  ApproveEventTypeId?: number;
  ApproveGroupId?: number;
  RejectStatusId: number;
  RejectEventTypeId?: number;
  RejectGroupId?: number;
  PendingStatusId: number;
  PendingEventTypeId?: number;
  PendingGroupId?: number;
  WorkFlowSetupId: number;
}

export interface UpdateApprovalLevelRequest {
  Id: number;
  Level: number;
  ApprovedById: number;
  ApproveStatusId: number;
  ApproveEventTypeId?: number;
  ApproveGroupId?: number;
  RejectStatusId: number;
  RejectEventTypeId?: number;
  RejectGroupId?: number;
  PendingStatusId: number;
  PendingEventTypeId?: number;
  PendingGroupId?: number;
  WorkFlowSetupId: number;
}

export interface WorkflowListResponse {
  data: Workflow[];
  total: number;
}

export interface WorkflowResponse {
  data: Workflow;
}

function ensureArray<T>(value: any): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && Array.isArray(value.data)) return value.data as T[];
  return [] as T[];
}

// API Service
export const workflowApi = {
  // Workflow CRUD Operations
  getAll: async (): Promise<Workflow[]> => {
    const res = await api.get<any>('/workflowsetup');
    return ensureArray<Workflow>(res);
  },

  getById: (id: number): Promise<Workflow> =>
    api.get(`/workflowsetup/${id}`),

  create: (workflow: CreateWorkflowRequest): Promise<Workflow> =>
    api.post('/workflowsetup', workflow),

  update: (workflow: UpdateWorkflowRequest): Promise<Workflow> =>
    api.put(`/workflowsetup/${workflow.Id}`, workflow),

  delete: (id: number): Promise<void> =>
    api.delete(`/workflowsetup/${id}`),

  // Approval Level Operations
  createApprovalLevel: (level: CreateApprovalLevelRequest): Promise<ApprovalLevel> =>
    api.post('/workflowapproval', level),

  updateApprovalLevel: (level: UpdateApprovalLevelRequest): Promise<ApprovalLevel> =>
    api.put(`/workflowapproval/${level.Id}`, level),

  deleteApprovalLevel: (id: number): Promise<void> =>
    api.delete(`/workflowapproval/${id}`),

  getApprovalLevelsByWorkflowId: async (workflowId: number): Promise<ApprovalLevel[]> => {
    const res = await api.get<any>(`/workflowapproval?WorkFlowSetupId=${workflowId}`);
    return ensureArray<ApprovalLevel>(res);
  },

  // Related Data Operations
  getRoles: async (): Promise<Role[]> => {
    const res = await api.get<any>('/role');
    return ensureArray<Role>(res);
  },

  getUsers: async (): Promise<User[]> => {
    const res = await api.get<any>('/user');
    return ensureArray<User>(res);
  },

  getStatuses: async (): Promise<Status[]> => {
    const res = await api.get<any>('/status');
    return ensureArray<Status>(res);
  },

  getEventTypes: async (): Promise<EventType[]> => {
    const res = await api.get<any>('/eventtype');
    return ensureArray<EventType>(res);
  },

  getGroups: async (): Promise<Group[]> => {
    const res = await api.get<any>('/group');
    return ensureArray<Group>(res);
  },

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<void> =>
    api.post('/workflowsetup/bulk/activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<void> =>
    api.post('/workflowsetup/bulk/deactivate', { ids }),

  // Statistics
  getStatistics: async (): Promise<{ total: number; active: number; inactive: number }> => {
    try {
      return await api.get('/workflowsetup/statistics');
    } catch (err) {
      const list = await workflowApi.getAll();
      const total = list.length;
      const active = list.filter(w => w.Active).length;
      const inactive = total - active;
      return { total, active, inactive };
    }
  },

  // Validation helpers
  validation: {
    validateWorkflow: (workflow: Partial<CreateWorkflowRequest> | Partial<UpdateWorkflowRequest> | Partial<Workflow>): string[] => {
      const errors: string[] = [];

      if (!workflow.ProcessName || workflow.ProcessName.trim().length === 0) {
        errors.push('Process Name is required');
      }

      if (workflow.ProcessName && workflow.ProcessName.trim().length > 255) {
        errors.push('Process Name must be 255 characters or less');
      }

      if (!workflow.Roles || workflow.Roles.length === 0) {
        errors.push('At least one role must be selected');
      }

      return errors;
    },

    validateApprovalLevel: (level: Partial<CreateApprovalLevelRequest> | Partial<UpdateApprovalLevelRequest> | Partial<ApprovalLevel>): string[] => {
      const errors: string[] = [];

      if (!level.ApprovedById || level.ApprovedById <= 0) {
        errors.push('Approved By role is required');
      }

      if (!level.ApproveStatusId || level.ApproveStatusId <= 0) {
        errors.push('Approve Status is required');
      }

      if (!level.RejectStatusId || level.RejectStatusId <= 0) {
        errors.push('Reject Status is required');
      }

      if (!level.PendingStatusId || level.PendingStatusId <= 0) {
        errors.push('Pending Status is required');
      }

      // Check for duplicate status IDs
      const statusIds = [level.ApproveStatusId, level.RejectStatusId, level.PendingStatusId].filter(Boolean);
      if (new Set(statusIds).size !== statusIds.length) {
        errors.push('Approve, Reject, and Pending statuses must be different');
      }

      // Check for duplicate event type IDs (if provided)
      const eventIds = [level.ApproveEventTypeId, level.RejectEventTypeId, level.PendingEventTypeId].filter(Boolean);
      if (eventIds.length > 0 && new Set(eventIds).size !== eventIds.length) {
        errors.push('Approve, Reject, and Pending events must be different when specified');
      }

      // Check for duplicate group IDs (if provided)
      const groupIds = [level.ApproveGroupId, level.RejectGroupId, level.PendingGroupId].filter(Boolean);
      if (groupIds.length > 0 && new Set(groupIds).size !== groupIds.length) {
        errors.push('Approve, Reject, and Pending groups must be different when specified');
      }

      return errors;
    },

    validateBeforeSave: (workflow: CreateWorkflowRequest | UpdateWorkflowRequest): string[] => {
      const errors = workflowApi.validation.validateWorkflow(workflow);

      // Additional validation for save operations
      if (workflow.Roles && workflow.Roles.some(role => typeof role.Id !== 'number' || role.Id <= 0)) {
        errors.push('Please select valid roles');
      }

      if (workflow.ApprovalLevels && workflow.ApprovalLevels.length > 0) {
        workflow.ApprovalLevels.forEach((level, index) => {
          const levelErrors = workflowApi.validation.validateApprovalLevel(level);
          levelErrors.forEach(error => {
            errors.push(`Level ${index + 1}: ${error}`);
          });
        });
      }

      return errors;
    }
  }
};

export default workflowApi; 